import {createActions, createReducer} from "reduxsauce";
import {
  readSessionStorageJson,
  writeSessionStorageJson,
} from "util/sessionStorageJson";

type initial = {
  definedRanges: (Date | null)[];
  dateFilter: string;
  currentActivityType: string;
  companyName:
    | {id?: number; label: string}
    | {id?: number; label: string}[];
  dashboardListView: boolean;
  refNext: string;
  responsible: {id?: number; label: string};
  task?: {id?: number; label: string}[];
  clientClassification?: {id?: number; label: string};
  showIaDashboard?: boolean;
};

type StoredDashboardFilters = Omit<initial, "definedRanges"> & {
  definedRanges?: (Date | null)[];
};

const DASHBOARD_FILTERS_STORAGE_KEY = "dashboardFilters";

const DEFAULT_STATE: initial = {
  definedRanges: [new Date(), null],
  dateFilter: "Hoje",
  currentActivityType: "",
  companyName: [],
  dashboardListView: false,
  refNext: "",
  responsible: {id: undefined, label: ""},
  task: [],
  clientClassification: {id: undefined, label: ""},
  showIaDashboard: false,
};

const restoreDefinedRanges = (
  definedRanges?: (Date | null)[],
): initial["definedRanges"] => {
  if (!Array.isArray(definedRanges) || definedRanges.length === 0) {
    return DEFAULT_STATE.definedRanges;
  }

  const [startDate, endDate] = definedRanges;

  return [
    startDate ? new Date(startDate) : null,
    endDate ? new Date(endDate) : null,
  ];
};

const normalizeStoredState = (storedState: StoredDashboardFilters): initial => {
  return {
    ...DEFAULT_STATE,
    ...storedState,
    definedRanges: restoreDefinedRanges(storedState?.definedRanges),
  };
};

const persistState = (state: initial): initial => {
  writeSessionStorageJson(DASHBOARD_FILTERS_STORAGE_KEY, {
    ...state,
    definedRanges: state.definedRanges.map((date) =>
      date instanceof Date ? date.toISOString() : null,
    ),
  });

  return state;
};

const INITIAL_STATE: initial = normalizeStoredState(
  readSessionStorageJson<StoredDashboardFilters>(
    DASHBOARD_FILTERS_STORAGE_KEY,
    DEFAULT_STATE,
  ),
);

export const {Types, Creators} = createActions({
  setDefinedRanges: ["definedRanges"],
  setDateFilter: ["dateFilter"],
  setCurrentActivityType: ["currentActivityType"],
  setCompanyName: ["companyName"],
  setDashboardView: ["dashboardListView"],
  setRefNext: ["refNext"],
  setResponsible: ["responsible"],
  setTask: ["task"],
  setClientClassification: ["clientClassification"],
  setShowIaDashboard: ["showIaDashboard"],
});

const setDefinedRanges = (state = INITIAL_STATE, action: initial) =>
  persistState({
    ...state,
    definedRanges: action.definedRanges,
  });

const setDateFilter = (state = INITIAL_STATE, action: initial) =>
  persistState({
    ...state,
    dateFilter: action.dateFilter,
  });

const setCurrentActivityType = (state = INITIAL_STATE, action: initial) =>
  persistState({
    ...state,
    currentActivityType: action.currentActivityType,
  });

const setCompanyName = (state = INITIAL_STATE, action: initial) =>
  persistState({
    ...state,
    companyName: action.companyName,
  });

const setDashboardView = (state = INITIAL_STATE, action: initial) =>
  persistState({
    ...state,
    dashboardListView: action.dashboardListView,
  });

const setRefNext = (state = INITIAL_STATE, action: initial) =>
  persistState({
    ...state,
    refNext: action.refNext,
  });

const setResponsible = (state = INITIAL_STATE, action: initial) =>
  persistState({
    ...state,
    responsible: action.responsible,
  });

const setTask = (state = INITIAL_STATE, action: initial) =>
  persistState({
    ...state,
    task: action.task,
  });

const setClientClassification = (state = INITIAL_STATE, action: initial) =>
  persistState({
    ...state,
    clientClassification: action.clientClassification,
  });

const setShowIaDashboard = (state = INITIAL_STATE, action: initial) =>
  persistState({
    ...state,
    showIaDashboard: action.showIaDashboard,
  });

export default createReducer(INITIAL_STATE, {
  [Types.SET_DEFINED_RANGES]: setDefinedRanges,
  [Types.SET_DATE_FILTER]: setDateFilter,
  [Types.SET_CURRENT_ACTIVITY_TYPE]: setCurrentActivityType,
  [Types.SET_COMPANY_NAME]: setCompanyName,
  [Types.SET_DASHBOARD_VIEW]: setDashboardView,
  [Types.SET_REF_NEXT]: setRefNext,
  [Types.SET_RESPONSIBLE]: setResponsible,
  [Types.SET_TASK]: setTask,
  [Types.SET_CLIENT_CLASSIFICATION]: setClientClassification,
  [Types.SET_SHOW_IA_DASHBOARD]: setShowIaDashboard,
});
